import { Download, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import type { InsuredDocumentRow } from "@/lib/api";
import { formatHumanFileSize, isImageMime, isPdfMime } from "./document-helpers";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  document: InsuredDocumentRow | null;
}

export function DocumentViewerDialog({ open, onOpenChange, document: doc }: Props) {
  if (!doc) {
    return null;
  }

  const viewUrl = `/api/documents/${doc.id}/view`;
  const downloadUrl = `/api/documents/${doc.id}/download`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-4xl"
        data-testid="dialog-document-viewer"
        aria-describedby={undefined}
      >
        <DialogHeader>
          <DialogTitle data-testid="viewer-title">{doc.name}</DialogTitle>
          <DialogDescription data-testid="viewer-subtitle">
            {doc.fileName} • {formatHumanFileSize(doc.fileSize ?? 0)}
          </DialogDescription>
        </DialogHeader>

        <div className="border rounded-md bg-muted/20 min-h-[420px] flex items-center justify-center overflow-hidden">
          {isPdfMime(doc.mimeType) ? (
            <iframe
              src={viewUrl}
              className="w-full h-[60vh]"
              title={doc.name}
              data-testid="viewer-pdf"
            />
          ) : isImageMime(doc.mimeType) ? (
            <img
              src={viewUrl}
              alt={doc.name}
              className="max-h-[60vh] max-w-full object-contain"
              data-testid="viewer-image"
            />
          ) : (
            <div
              className="flex flex-col items-center gap-2 p-8 text-center text-sm text-muted-foreground"
              data-testid="viewer-fallback"
            >
              <p>Preview not available for this file type. Use the download button below.</p>
            </div>
          )}
        </div>

        <DialogFooter>
          <a
            href={downloadUrl}
            download={doc.fileName}
            className="inline-flex items-center gap-2 rounded-md border px-4 py-2 text-sm font-medium hover:bg-muted/50 transition-colors"
            data-testid="button-download-document"
          >
            <Download className="w-4 h-4" aria-hidden="true" />
            Download
          </a>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="button-close-viewer"
          >
            <X className="w-4 h-4 mr-2" aria-hidden="true" />
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
